import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Form, InputGroup, Card, Container, Alert } from 'react-bootstrap';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { requestChangePassword, verifyOtp } from '../../../redux/actions/Master.action';

export default function ChangePasswordRequest() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const subAdminState = useSelector((state) => state.master);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isPasswordSubmitted, setIsPasswordSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const toggleOldPasswordVisibility = () => setShowOldPassword(!showOldPassword);
    const toggleNewPasswordVisibility = () => setShowNewPassword(!showNewPassword);
    const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

    const validationSchema = Yup.object().shape({
        oldPassword: Yup.string().required('Old password is required'),
        newPassword: Yup.string()
            .min(6, 'New password must be at least 6 characters')
            .notOneOf([Yup.ref('oldPassword')], 'New password must be different from old password')
            .required('New password is required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
            .required('Confirm password is required'),
        otp: isPasswordSubmitted
            ? Yup.string()
                  .matches(/^\d{6}$/, 'OTP must be a 6-digit number')
                  .required('OTP is required')
            : Yup.string(),
    });

    const initialValues = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
        otp: '',
    };

    const handleSubmit = (values, { resetForm, setSubmitting }) => {
        if (!isPasswordSubmitted) {
            const payload = {
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
            };
            dispatch(requestChangePassword(payload)).then((res) => {
                if (res?.status === 200) {
                    setIsPasswordSubmitted(true);
                    setSuccessMessage('OTP sent successfully. Please check your email.');
                } else {
                    setErrorMessage('Failed to send OTP. Please try again.');
                }
                setSubmitting(false);
            }).catch(() => {
                setErrorMessage('An error occurred. Please try again.');
                setSubmitting(false);
            });
        } else {
            const payload = {
                otp: values.otp,
            };
            dispatch(verifyOtp(payload)).then((res) => {
                if (res?.status === 200) {
                    setSuccessMessage('Password changed successfully!');
                    localStorage.removeItem('isAuthenticated');
                    localStorage.removeItem('token');
                    localStorage.removeItem('type');
                    localStorage.removeItem('logo');
                    localStorage.removeItem('chakra-ui-color-mode');
                    setIsPasswordSubmitted(false);
                    resetForm();
                    setTimeout(() => {
                        navigate('/auth/sign-in');
                    }, 2000);
                } else {
                    setErrorMessage('Invalid OTP. Please try again.');
                }
                setSubmitting(false);
            }).catch(() => {
                setErrorMessage('An error occurred during OTP verification.');
                setSubmitting(false);
            });
        }
    };

    return (
        <Container className="d-flex align-items-center justify-content-center min-vh-100">
            <Card className="shadow-lg w-100" style={{ maxWidth: '500px' }}>
                <Card.Body className="p-5">
                    <div className="text-center mb-4">
                        <h3 className="fw-bold mb-1">Change Password</h3>
                        <p className="text-muted">Securely update your account password</p>
                    </div>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ handleSubmit, setFieldValue, values, isSubmitting }) => (
                            <Form onSubmit={handleSubmit}>
                                {!isPasswordSubmitted ? (
                                    <>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Current Password</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type={showOldPassword ? 'text' : 'password'}
                                                    name="oldPassword"
                                                    onChange={(e) => setFieldValue('oldPassword', e.target.value)}
                                                    value={values.oldPassword}
                                                    placeholder="Enter current password"
                                                    className="shadow-none rounded-start"
                                                    disabled={isSubmitting}
                                                />
                                                <InputGroup.Text
                                                    onClick={toggleOldPasswordVisibility}
                                                    className="cursor-pointer bg-light"
                                                >
                                                    {showOldPassword ? <EyeSlash /> : <Eye />}
                                                </InputGroup.Text>
                                            </InputGroup>
                                            <ErrorMessage
                                                name="oldPassword"
                                                component="div"
                                                className="text-danger mt-1 small"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">New Password</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    name="newPassword"
                                                    onChange={(e) => setFieldValue('newPassword', e.target.value)}
                                                    value={values.newPassword}
                                                    placeholder="Enter new password"
                                                    className="shadow-none rounded-start"
                                                    disabled={isSubmitting}
                                                />
                                                <InputGroup.Text
                                                    onClick={toggleNewPasswordVisibility}
                                                    className="cursor-pointer bg-light"
                                                >
                                                    {showNewPassword ? <EyeSlash /> : <Eye />}
                                                </InputGroup.Text>
                                            </InputGroup>
                                            <ErrorMessage
                                                name="newPassword"
                                                component="div"
                                                className="text-danger mt-1 small"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Confirm New Password</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    name="confirmPassword"
                                                    onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                                                    value={values.confirmPassword}
                                                    placeholder="Confirm new password"
                                                    className="shadow-none rounded-start"
                                                    disabled={isSubmitting}
                                                />
                                                <InputGroup.Text
                                                    onClick={toggleConfirmPasswordVisibility}
                                                    className="cursor-pointer bg-light"
                                                >
                                                    {showConfirmPassword ? <EyeSlash /> : <Eye />}
                                                </InputGroup.Text>
                                            </InputGroup>
                                            <ErrorMessage
                                                name="confirmPassword"
                                                component="div"
                                                className="text-danger mt-1 small"
                                            />
                                        </Form.Group>
                                    </>
                                ) : (
                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-semibold">Enter OTP</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="otp"
                                            onChange={(e) => setFieldValue('otp', e.target.value)}
                                            value={values.otp}
                                            placeholder="Enter 6-digit OTP"
                                            className="shadow-none"
                                            disabled={isSubmitting}
                                        />
                                        <ErrorMessage
                                            name="otp"
                                            component="div"
                                            className="text-danger mt-1 small"
                                        />
                                    </Form.Group>
                                )}

                                <div className="d-flex justify-content-end align-items-center mt-4">
                                    <Button
                                        type="submit"
                                        className="px-4 py-2"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Processing...' : isPasswordSubmitted ? 'Verify OTP' : 'Submit'}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Card.Body>
            </Card>
        </Container>
    );
}